import React, { useEffect, useState } from 'react';
import { getTransactions, categorizeTransactions, clearData } from '../services/api';

export default function Transactions({ refreshKey }) {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [catLoading, setCatLoading] = useState(false);
  const [catResult, setCatResult] = useState(null);
  const [search, setSearch] = useState('');
  const LIMIT = 20;

  const fetchData = () => {
    setLoading(true);
    getTransactions(LIMIT, page * LIMIT)
      .then((r) => {
        setTransactions(r.data.transactions || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => setTransactions([]))
      .finally(() => setLoading(false));
  };

  useEffect(fetchData, [page, refreshKey]);

  const handleCategorize = async () => {
    setCatLoading(true);
    setCatResult(null);
    try {
      const r = await categorizeTransactions();
      setCatResult(r.data);
      fetchData();
    } catch {
      setCatResult({ message: 'Failed to categorize' });
    } finally {
      setCatLoading(false);
    }
  };

  const handleClearData = async () => {
    if (!window.confirm('Are you sure you want to clear all transaction data?')) return;
    try {
      await clearData();
      fetchData();
    } catch {}
  };

  const filtered = transactions.filter(
    (t) =>
      !search ||
      t.description?.toLowerCase().includes(search.toLowerCase()) ||
      t.category?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Transactions</h2>
          <p className="text-sm text-slate-400">{total} total transactions</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCategorize}
            disabled={catLoading || total === 0}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow-md transition disabled:opacity-50"
          >
            {catLoading ? 'Categorizing…' : '🏷️ Auto-Categorize'}
          </button>
          <button
            onClick={handleClearData}
            disabled={total === 0}
            className="px-4 py-2 bg-red-50 text-red-600 text-sm font-medium rounded-lg hover:bg-red-100 transition disabled:opacity-50"
          >
            🗑️ Clear All
          </button>
        </div>
      </div>

      {catResult && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg">
          {catResult.message}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search transactions…"
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
        />
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="skeleton h-10 rounded" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            {total === 0 ? 'No transactions yet. Upload a CSV to get started!' : 'No matching transactions.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-6 py-3">ID</th>
                  <th className="px-6 py-3">Date</th>
                  <th className="px-6 py-3">Description</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-blue-50/40 transition">
                    <td className="px-6 py-3.5 text-slate-400 font-mono text-xs">#{t.id}</td>
                    <td className="px-6 py-3.5 text-slate-500 whitespace-nowrap">{t.date || '—'}</td>
                    <td className="px-6 py-3.5 font-medium text-slate-700 max-w-xs truncate">{t.description}</td>
                    <td className="px-6 py-3.5">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        t.category === 'Uncategorized'
                          ? 'bg-slate-100 text-slate-500'
                          : 'bg-blue-50 text-blue-700'
                      }`}>
                        {t.category}
                      </span>
                    </td>
                    <td className={`px-6 py-3.5 text-right font-semibold whitespace-nowrap ${
                      t.amount >= 0 ? 'text-emerald-600' : 'text-red-500'
                    }`}>
                      {t.amount >= 0 ? '+' : '-'}${Math.abs(t.amount).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
            <p className="text-xs text-slate-400">
              Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}
            </p>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1.5 text-sm bg-white border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition"
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-1.5 text-sm bg-white border rounded-lg disabled:opacity-40 hover:bg-slate-50 transition"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
